import { useState, useEffect, useCallback } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import moment from "moment";
import { api } from "../../services/apiService";

const STATUS_CHIP = {
  OPEN: { label: "New", cls: "tag-chip--open" },
  ACKNOWLEDGED: { label: "Acknowledged", cls: "tag-chip--ack" },
  RESOLVED: { label: "Resolved", cls: "tag-chip--resolved" },
};

/**
 * Tutor-side "Tagged questions" (design §10.4, slice T4) — rendered on
 * /mentorships/students/{id} above the history table, tutors only, flag-gated
 * by the caller. Lists the tags THIS student addressed to the caller
 * (server-scoped: the list is keyed tutorUserId = caller server-side per
 * §10.3-R1 and joined against a currently-ACTIVE link per R2 — a revoked link
 * makes every tag vanish on the next request, and this component renders
 * whatever the server says, never a cache). Acknowledge/Resolve are
 * row-resolved server-side (M1); the context view (question + the student's
 * attempts) rides VIEW_PROGRESS (R3).
 *
 * XSS discipline (§10.3, same as T3): the student's note — and every string
 * from the context read (question stem, the student's answer) — renders ONLY
 * through JSX text nodes (React output-encodes); no dangerouslySetInnerHTML.
 * This is the higher-risk direction: student-authored text rendering in the
 * TUTOR's account.
 *
 * Uniform error UX (§10.3-N4): the server answers ONE 404 for missing /
 * not-yours / revoked on every by-id route. This UI mirrors that — every
 * acknowledge/resolve failure shows the same generic message, and every
 * context failure shows the same generic message, adding no existence detail.
 */
export default function TaggedQuestionsSection({ studentUserId, studentName }) {
  const [tags, setTags] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  // Context dialog: null = closed, {tagId} while loading, then {tagId, data}
  // or {tagId, failed} — one state object so a re-open always starts fresh.
  const [ctx, setCtx] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api.listQuestionTags({ counterpartUserId: studentUserId });
      const status = res?.errStatus || res?.status;
      if (status === 404) {
        // The single dark seam: backend flags off — render nothing, not an error.
        setTags([]);
        return;
      }
      if (Array.isArray(res)) {
        setTags(res);
      } else {
        setError("Couldn't load tagged questions. Please try again.");
        setTags(null);
      }
    } catch (e) {
      setError("Couldn't load tagged questions. Please try again.");
      setTags(null);
    }
  }, [studentUserId]);

  useEffect(() => {
    load();
  }, [load]);

  const transition = async (id, op) => {
    setBusyId(id);
    setError("");
    let failed = false;
    try {
      // doUpdateCall resolves {errStatus, errMsg} on failure — inspect, don't catch.
      const res =
        op === "acknowledge"
          ? await api.acknowledgeQuestionTag(id)
          : await api.resolveQuestionTag(id);
      failed = !!(res?.errStatus || res?.errMsg);
    } catch (e) {
      failed = true;
    }
    // Reload regardless: the server is authoritative, and after a revoke the
    // refetch is what makes the whole section disappear (R2).
    await load();
    setBusyId("");
    // Uniform copy for EVERY failure — the server's 404 is deliberately
    // identical for missing/not-yours/revoked and the UI adds no detail.
    if (failed) setError("Couldn't update that tag. Please try again.");
  };

  const openContext = async (id) => {
    setCtx({ tagId: id });
    try {
      const res = await api.getQuestionTagContext(id);
      // Success = the context DTO ({tag, attempts}); doGetCall's failure shape
      // ({status, errMsg}) never carries a tag. Tolerate a missing attempts
      // array — an empty context is a valid read, not an error.
      if (res?.tag) {
        const attempts = Array.isArray(res.attempts) ? res.attempts : [];
        setCtx((cur) => (cur?.tagId === id ? { tagId: id, data: { tag: res.tag, attempts } } : cur));
      } else {
        // Uniform for 403 (no VIEW_PROGRESS) / 404 (missing/not-yours/revoked)
        // / anything else — no existence or permission detail leaks.
        setCtx((cur) => (cur?.tagId === id ? { tagId: id, failed: true } : cur));
      }
    } catch (e) {
      setCtx((cur) => (cur?.tagId === id ? { tagId: id, failed: true } : cur));
    }
  };

  const items = tags || [];
  if (tags !== null && items.length === 0 && !error) {
    return null; // no tags from this student — stay out of the page's way
  }

  const attempts = ctx?.data?.attempts || [];
  const stem = attempts.find((a) => a?.questionStem)?.questionStem;

  return (
    <div className="mtr-section" data-testid="tut-tag-section">
      <Typography variant="subtitle2" className="mtr-section-title">
        Tagged questions
      </Typography>
      <Typography variant="caption" className="mtr-sub">
        Questions {studentName || "this student"} flagged for you to review together.
      </Typography>
      {error && (
        <Typography className="mtr-error" variant="body2" data-testid="tut-tag-error">
          {error}
        </Typography>
      )}
      {tags === null && !error && (
        <div className="mtr-loading">
          <CircularProgress size={20} />
        </div>
      )}
      {items.map((t) => {
        const chip = STATUS_CHIP[t.status] || { label: t.status, cls: "" };
        return (
          <div className="mtr-card mtr-item" key={t.id} data-testid="tut-tag-row">
            <div className="mtr-item-main">
              <div className="tag-row-head">
                <Chip size="small" className={`tag-chip ${chip.cls}`} label={chip.label} />
                <Typography variant="caption" className="mtr-sub">
                  {t.createdAt && moment(t.createdAt).isValid()
                    ? moment(t.createdAt).format("ll")
                    : ""}
                </Typography>
              </div>
              {/* Student-authored note in the TUTOR's account: renders as a JSX
                  TEXT NODE only — React output-encodes it, so a <script>/onerror
                  payload stored in the note is inert text here too. */}
              {t.note && (
                <Typography variant="body2" className="mtr-msg" data-testid="tut-tag-note">
                  “{t.note}”
                </Typography>
              )}
            </div>
            <div className="mtr-item-actions">
              <Button
                className="mtr-btn"
                variant="outlined"
                size="small"
                disabled={busyId === t.id}
                onClick={() => openContext(t.id)}
                data-testid="tut-tag-view"
              >
                View question
              </Button>
              {t.status === "OPEN" && (
                <Button
                  className="mtr-btn"
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={busyId === t.id}
                  onClick={() => transition(t.id, "acknowledge")}
                  data-testid="tut-tag-ack"
                >
                  Acknowledge
                </Button>
              )}
              {(t.status === "OPEN" || t.status === "ACKNOWLEDGED") && (
                <Button
                  className="mtr-btn"
                  variant="outlined"
                  size="small"
                  disabled={busyId === t.id}
                  onClick={() => transition(t.id, "resolve")}
                  data-testid="tut-tag-resolve"
                >
                  Resolve
                </Button>
              )}
            </div>
          </div>
        );
      })}

      <Dialog open={!!ctx} onClose={() => setCtx(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Tagged question</DialogTitle>
        <DialogContent>
          {ctx && !ctx.data && !ctx.failed && (
            <CircularProgress size={22} data-testid="tag-ctx-loading" />
          )}
          {ctx?.failed && (
            <Typography variant="body2" color="error" data-testid="tag-ctx-error">
              Couldn't load the question details. Please try again.
            </Typography>
          )}
          {ctx?.data && (
            <>
              {/* Question stem + the student's answer are rendered as JSX text
                  nodes only — same output-encoding discipline as the note. */}
              {stem && (
                <Typography variant="body2" className="tag-ctx-stem" data-testid="tag-ctx-stem">
                  {stem}
                </Typography>
              )}
              {attempts.length === 0 && (
                <Typography variant="body2" className="mtr-sub" data-testid="tag-ctx-empty">
                  No attempt details are available for this question.
                </Typography>
              )}
              {attempts.map((a, i) => (
                <div className="tag-ctx-attempt" key={i} data-testid="tag-ctx-attempt">
                  <Typography variant="body2">
                    Their answer: {a.answerGiven || "—"}{" "}
                    <span className={a.correct ? "tag-ctx-correct" : "tag-ctx-incorrect"}>
                      ({a.correct ? "correct" : "incorrect"})
                    </span>
                  </Typography>
                  <Typography variant="caption" className="mtr-sub">
                    {a.attemptedAt && moment(a.attemptedAt).isValid()
                      ? `Attempted ${moment(a.attemptedAt).format("ll")}`
                      : ""}
                    {(() => {
                      const cats = Object.values(a.categories || {})
                        .filter(Array.isArray)
                        .flat()
                        .join(", ");
                      return cats ? ` · ${cats}` : "";
                    })()}
                  </Typography>
                </div>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCtx(null)} data-testid="tag-ctx-close">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
